"use client";

import { ArrowRight, BookOpen, DownloadSimple, ShareNetwork, Fire } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import type { Locale } from "@/i18n/config";
import {
  getWisdomTranslationOption,
  getWisdomTranslationText,
  hasWisdomTranslation,
  WISDOM_TRANSLATION_GROUPS,
  type WisdomLanguageKey,
  type WisdomScriptureText,
  type WisdomTranslationCode,
} from "@/lib/wisdomTranslations";
import { useAppDialog } from "@/components/providers/app-dialog-provider";

// Types matching the API response
type ScriptureTranslation = WisdomScriptureText;

interface WisdomEntry {
  day: number;
  date: string;
  imageUrl: string;
  scripture: string;
  scriptures: ScriptureTranslation[];
}

interface WisdomAPIResponse {
  latestDay: number;
  totalDays: number;
  wisdoms: WisdomEntry[];
}

type WisdomSectionTranslations = {
  sectionLabel: string;
  title: string;
  titleHighlight: string;
  description: string;
  browseDays: string;
  viewArchive: string;
  scriptureReading: string;
  english: string;
  french: string;
  spanish?: string;
  creole?: string;
  download: string;
  share: string;
  day: string;
  noTranslation: string;
  comingSoon?: string;
  linkCopied?: string;
  shareTitlePrefix?: string;
};

type WisdomSectionProps = {
  locale?: Locale;
  translations?: WisdomSectionTranslations;
};

const numberToWord = (num: number): string => {
  const words = [
    'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen', 'Twenty',
    'Twenty-One', 'Twenty-Two', 'Twenty-Three', 'Twenty-Four', 'Twenty-Five', 'Twenty-Six', 'Twenty-Seven', 'Twenty-Eight', 'Twenty-Nine', 'Thirty',
    'Thirty-One', 'Thirty-Two', 'Thirty-Three', 'Thirty-Four', 'Thirty-Five', 'Thirty-Six', 'Thirty-Seven', 'Thirty-Eight', 'Thirty-Nine', 'Forty'
  ];
  return words[num - 1] || num.toString();
};

// Prefetch upcoming day images through the Next image optimizer.
// Never prefetch the raw imageUrl: the trajectory originals are multi-MB JPEGs
// and fetching them directly added ~6 MB to the homepage load.
const prefetchImages = (urls: string[]) => {
  const warm = () => {
    urls.forEach((url) => {
      const img = new window.Image();
      img.src = `/_next/image?url=${encodeURIComponent(url)}&w=1080&q=75`;
    });
  };
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(warm, { timeout: 3000 });
  } else {
    setTimeout(warm, 2000);
  }
};

// English fallback used only when the parent fails to pass translations.
// Keep keys in sync with the `wisdomSection` namespace in src/i18n/dictionaries/*.json.
const DEFAULT_WISDOM_TRANSLATIONS: Required<WisdomSectionTranslations> & {
  comingSoon: string;
  linkCopied: string;
  shareTitlePrefix: string;
} = {
  sectionLabel: "Daily Devotional",
  title: "Scriptural",
  titleHighlight: "Wisdom",
  description: "Daily words to ignite and strengthen your faith journey",
  browseDays: "Browse Days",
  viewArchive: "View Full Archive",
  scriptureReading: "Scripture Reading",
  english: "English",
  french: "Français",
  spanish: "Español",
  creole: "Kreyòl",
  download: "Download",
  share: "Share",
  day: "Day",
  noTranslation: "Translation not available for this day.",
  comingSoon: "Coming soon...",
  linkCopied: "Link copied to clipboard!",
  shareTitlePrefix: "Scriptural Wisdom - Day",
};

export const WisdomSection = ({ locale = 'en', translations }: WisdomSectionProps) => {
  const { alert } = useAppDialog();
  const t = { ...DEFAULT_WISDOM_TRANSLATIONS, ...(translations ?? {}) };

  const [wisdoms, setWisdoms] = useState<WisdomEntry[]>([]);
  const [selectedDay, setSelectedDay] = useState<WisdomEntry | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [scriptureVersion, setScriptureVersion] = useState<WisdomTranslationCode>('kjv');
  const [isLoading, setIsLoading] = useState(true);

  const languageLabels: Record<WisdomLanguageKey, string> = {
    english: t.english,
    french: t.french,
    spanish: t.spanish ?? "Español",
    creole: t.creole ?? "Kreyol",
  };

  // Fetch wisdom data from API and prefetch first 4 images
  useEffect(() => {
    const fetchWisdoms = async () => {
      try {
        const response = await fetch('https://winnerschapelcanada.ca/api/wisdom');
        if (response.ok) {
          const data: WisdomAPIResponse = await response.json();
          setWisdoms(data.wisdoms);
          setSelectedDay(data.wisdoms[0]);

          // Warm days 2-4 for faster browsing; day 1 is already rendered by next/image
          const imagesToPrefetch = data.wisdoms.slice(1, 4).map((w) => w.imageUrl);
          prefetchImages(imagesToPrefetch);
        }
      } catch (error) {
        console.error('Failed to fetch wisdom data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchWisdoms();
  }, []);

  const sidebarDays = wisdoms.slice(0, 4);

  const handleDayClick = (wisdom: WisdomEntry) => {
    setImageLoaded(false);
    setSelectedDay(wisdom);
  };

  useEffect(() => {
    if (!selectedDay) return;

    const option = getWisdomTranslationOption(scriptureVersion);
    const isNewOptionalLanguage =
      option?.languageKey === "spanish" || option?.languageKey === "creole";

    if (
      isNewOptionalLanguage &&
      !hasWisdomTranslation(selectedDay.scriptures, scriptureVersion)
    ) {
      setScriptureVersion("kjv");
    }
  }, [scriptureVersion, selectedDay]);

  const handleDownload = async () => {
    if (!selectedDay) return;
    try {
      const response = await fetch(selectedDay.imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `yaf-wisdom-day-${selectedDay.day}.jpeg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleShare = async () => {
    if (!selectedDay) return;
    const shareData = {
      title: `${t.shareTitlePrefix} ${selectedDay.day}`,
      text: `${t.day} ${selectedDay.day} - ${selectedDay.scripture}\n\nYouth Alive Fellowship Canada`,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Share failed:', error);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
        void alert(t.linkCopied, { variant: "success" });
      } catch (error) {
        console.error('Copy failed:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <section className="py-32 bg-zinc-50 dark:bg-black px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
          <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
        </div>
      </section>
    );
  }

  if (!selectedDay) {
    // Show placeholder if API fails or no data
    return (
      <section id="wisdom" className="py-32 bg-zinc-50 dark:bg-black px-6 relative overflow-hidden">
        <div className="max-w-7xl mx-auto text-center">
          <span className="text-brand-gold text-sm tracking-[0.4em] uppercase mb-6 block font-black">
            {t.sectionLabel}
          </span>
          <h2 className="font-serif text-6xl lg:text-8xl font-bold text-zinc-900 dark:text-white tracking-tighter mb-4">
            {t.title} <span className="text-brand-red italic">{t.titleHighlight}</span>
          </h2>
          <p className="text-xl text-zinc-500 dark:text-zinc-400 max-w-xl mx-auto">
            {t.comingSoon}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="wisdom" className="py-32 bg-zinc-50 dark:bg-black px-6 relative overflow-hidden">
      {/* Background fire effect - dark mode only */}
      <div className="absolute inset-0 opacity-0 dark:opacity-[0.03]">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gradient-radial from-brand-red via-transparent to-transparent blur-3xl" />
      </div>

      {/* Light mode subtle background pattern */}
      <div className="absolute inset-0 opacity-30 dark:opacity-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-gradient-radial from-brand-gold/10 via-transparent to-transparent blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-gradient-radial from-brand-red/5 via-transparent to-transparent blur-3xl" />
      </div>

      {/* Decorative text */}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none">
        <span className="text-[20vw] font-serif font-bold text-zinc-900/[0.03] dark:text-white/[0.02] tracking-tighter whitespace-nowrap select-none">
          TRAJECTORY
        </span>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-12 mb-20"
        >
          <div className="max-w-3xl">
            <span className="text-brand-gold text-sm tracking-[0.4em] uppercase mb-6 block font-black flex items-center gap-3">
              <Fire weight="fill" className="w-5 h-5" />
              {t.sectionLabel}
            </span>
            <h2 className="font-serif text-6xl lg:text-8xl font-bold leading-[0.85] text-zinc-900 dark:text-white tracking-tighter">
              {t.title} <br />
              <span className="text-brand-red italic">{t.titleHighlight}</span>
            </h2>
          </div>
          <p className="text-xl text-zinc-500 dark:text-zinc-400 max-w-md lg:text-right font-light leading-relaxed">
            {t.description}
          </p>
        </motion.div>

        {/* Main content */}
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          {/* Featured Image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-2"
          >
            <div className="relative p-[2px] rounded-3xl bg-gradient-to-br from-brand-gold/40 via-brand-red/20 to-brand-gold/40 shadow-2xl shadow-brand-red/10">
              <div className="relative rounded-[22px] overflow-hidden bg-zinc-200 dark:bg-zinc-900">
                {/* Loading skeleton */}
                {!imageLoaded && (
                  <div className="absolute inset-0 bg-zinc-300 dark:bg-zinc-800 animate-pulse" />
                )}

                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedDay.day}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="relative"
                  >
                    <Image
                      src={selectedDay.imageUrl}
                      alt={`Day ${selectedDay.day} - ${selectedDay.scripture}`}
                      width={800}
                      height={1000}
                      sizes="(max-width: 1024px) 100vw, 60vw"
                      className={`w-full h-auto transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                      onLoad={() => setImageLoaded(true)}
                      priority
                    />

                    {/* Top left action buttons */}
                    <div className="absolute top-4 left-4 flex gap-3">
                      <motion.button
                        onClick={handleDownload}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        aria-label={t.download}
                        className="bg-white/95 backdrop-blur-sm text-zinc-900 p-3 rounded-xl shadow-xl hover:bg-white transition-colors"
                      >
                        <DownloadSimple weight="bold" className="w-5 h-5" />
                      </motion.button>
                      <motion.button
                        onClick={handleShare}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        aria-label={t.share}
                        className="bg-white/95 backdrop-blur-sm text-zinc-900 p-3 rounded-xl shadow-xl hover:bg-white transition-colors"
                      >
                        <ShareNetwork weight="bold" className="w-5 h-5" />
                      </motion.button>
                    </div>
                  </motion.div>
                </AnimatePresence>

                {/* Day badge */}
                <div className="absolute top-4 right-4 bg-brand-red text-white px-4 py-2 rounded-xl font-black text-sm shadow-lg hidden sm:flex items-center gap-2">
                  <Fire weight="fill" className="w-4 h-4" />
                  {t.day} {selectedDay.day}
                </div>
              </div>
            </div>

            {/* Mobile day label */}
            <div className="sm:hidden flex justify-center mt-4">
              <span className="inline-flex items-center gap-2 bg-brand-red text-white px-4 py-2 rounded-xl font-black text-sm shadow-lg">
                <Fire weight="fill" className="w-4 h-4" />
                {t.day} {selectedDay.day}
              </span>
            </div>

            {/* Mobile action buttons - always visible */}
            <div className="sm:hidden flex justify-center gap-2 mt-4">
              <motion.button
                onClick={handleDownload}
                whileTap={{ scale: 0.95 }}
                aria-label={t.download}
                className="bg-zinc-900 dark:bg-white/95 text-white dark:text-zinc-900 p-2 rounded-lg shadow-lg"
              >
                <DownloadSimple weight="bold" className="w-4 h-4" />
              </motion.button>
              <motion.button
                onClick={handleShare}
                whileTap={{ scale: 0.95 }}
                aria-label={t.share}
                className="bg-zinc-900 dark:bg-white/95 text-white dark:text-zinc-900 p-2 rounded-lg shadow-lg"
              >
                <ShareNetwork weight="bold" className="w-4 h-4" />
              </motion.button>
            </div>
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-4"
          >
            {/* Browse Days */}
            <div className="bg-white dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl p-6 border border-zinc-200 dark:border-white/5 shadow-lg dark:shadow-none">
              <p className="text-brand-gold text-xs font-black uppercase tracking-[0.3em] mb-5">
                {t.browseDays}
              </p>

              <div className="space-y-2">
                {sidebarDays.map((wisdom) => {
                  const isSelected = selectedDay.day === wisdom.day;
                  return (
                    <button
                      key={wisdom.date}
                      onClick={() => handleDayClick(wisdom)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 group text-left ${
                        isSelected
                          ? 'bg-brand-red/10 dark:bg-brand-red/20 ring-2 ring-brand-red'
                          : 'hover:bg-zinc-100 dark:hover:bg-white/5 hover:ring-1 hover:ring-zinc-300 dark:hover:ring-white/10'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <span className={`w-10 h-10 flex items-center justify-center font-black rounded-xl text-sm ${
                          isSelected ? 'bg-brand-red text-white' : 'bg-brand-gold/20 text-brand-gold'
                        }`}>
                          {wisdom.day}
                        </span>
                        <span className={`font-bold transition-colors ${
                          isSelected ? 'text-zinc-900 dark:text-white' : 'text-zinc-500 dark:text-zinc-400 group-hover:text-zinc-900 dark:group-hover:text-white'
                        }`}>
                          {t.day} {locale === 'en' ? numberToWord(wisdom.day) : wisdom.day}
                        </span>
                      </div>
                      {isSelected ? (
                        <Fire weight="fill" className="w-5 h-5 text-brand-red" />
                      ) : (
                        <ArrowRight className="w-5 h-5 text-zinc-400 dark:text-zinc-600 group-hover:text-brand-gold transition-colors" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* View Archive Link */}
            <Link href={`/${locale}/wisdom`}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-gradient-to-r from-brand-red to-brand-red/80 hover:from-brand-red hover:to-brand-red text-white rounded-xl p-4 text-center font-black uppercase text-xs tracking-widest transition-all cursor-pointer shadow-lg shadow-brand-red/20 flex items-center justify-center gap-3"
              >
                {t.viewArchive}
                <ArrowRight weight="bold" className="w-4 h-4" />
              </motion.div>
            </Link>

            {/* Scripture Panel */}
            <div className="bg-white dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-zinc-200 dark:border-white/5 overflow-hidden shadow-lg dark:shadow-none">
              {/* Panel Header */}
              <div className="px-4 py-4 border-b border-zinc-200 dark:border-white/5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-brand-gold/20 rounded-xl flex items-center justify-center">
                    <BookOpen weight="fill" className="w-5 h-5 text-brand-gold" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-zinc-900 dark:text-white text-sm">{t.scriptureReading}</h3>
                    <p className="text-xs text-zinc-500 truncate">{selectedDay.scripture}</p>
                  </div>
                </div>

                {/* Version Toggle */}
                <div className="space-y-3">
                  {WISDOM_TRANSLATION_GROUPS.map((group) => (
                    <div key={group.languageKey}>
                      <p className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-black">
                        {languageLabels[group.languageKey]}
                      </p>
                      <div className="flex bg-zinc-100 dark:bg-black/50 rounded-lg p-1 mt-1">
                        {group.options.map((option) => {
                          const isOptionalNewLanguage =
                            group.languageKey === "spanish" ||
                            group.languageKey === "creole";
                          const isUnavailable =
                            isOptionalNewLanguage &&
                            !hasWisdomTranslation(selectedDay.scriptures, option.code);

                          return (
                            <button
                              key={option.code}
                              onClick={() => setScriptureVersion(option.code)}
                              disabled={isUnavailable}
                              className={`flex-1 px-3 py-2 text-xs font-bold rounded-lg transition-all ${
                                scriptureVersion === option.code
                                  ? 'bg-brand-gold text-black'
                                  : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white'
                              } ${isUnavailable ? 'cursor-not-allowed opacity-40 hover:text-zinc-500 dark:hover:text-zinc-500' : ''}`}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Scripture Content */}
              <div className="p-4 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-300 dark:scrollbar-thumb-zinc-700 scrollbar-track-transparent">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${selectedDay.day}-${scriptureVersion}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-4"
                  >
                    {selectedDay.scriptures.map((scripture, index) => {
                      const text = getWisdomTranslationText(scripture, scriptureVersion);
                      const noTranslation = !text;

                      return (
                        <div key={index} className="space-y-2">
                          <p className="text-xs font-black text-brand-gold uppercase tracking-wide">
                            {scripture.reference}
                          </p>
                          {noTranslation ? (
                            <p className="text-zinc-500 italic text-sm">
                              {t.noTranslation}
                            </p>
                          ) : (
                            <p className="text-zinc-600 dark:text-zinc-300 leading-relaxed text-sm font-light">
                              {text}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
